import React, { useCallback, useEffect, useState } from 'react';
import { Gift, Layers, LineChart, Table2, Ticket } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useAuth } from '../../hooks/useAuth';
import { Spinner } from '../../components/ui/Spinner';
import { PlanCatalogue, plansApi } from '../../api/plans';
import { PlanList } from './sections/PlanList';
import { FeatureMatrix } from './sections/FeatureMatrix';
import { Economics } from './sections/Economics';
import { Gifts } from './sections/Gifts';
import { Coupons } from './sections/Coupons';

type Tab = 'plans' | 'matrix' | 'gifts' | 'coupons' | 'economics';

/**
 * Plans & pricing.
 *
 * The catalogue the business app fetches at runtime, editable here. Prices
 * are deliberately not compiled into the app: a price in a mobile bundle
 * needs a release and a store review to change, and leaves older installs
 * quoting the previous number for weeks afterwards.
 */
export const Plans: React.FC = () => {
  usePage('Plans & Pricing');
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('plans');
  const [catalogue, setCatalogue] = useState<PlanCatalogue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Editing a price moves money, so it is a narrower grant than reading one.
  const canManage = can('plans.manage');

  /*
   * The first load shows a spinner; a reload after an edit does not. It used
   * to: every save set `loading`, which unmounted the list or the matrix,
   * threw away whatever it was showing (an error, a half-typed edit) and
   * sent the page back to the top.
   */
  const load = useCallback(async () => {
    try {
      setError(null);
      setCatalogue(await plansApi.getCatalogue());
    } catch {
      setError('Could not load the plan catalogue.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const tabs: { key: Tab; label: string; icon: typeof Layers }[] = [
    { key: 'plans', label: 'Plans', icon: Layers },
    { key: 'matrix', label: 'Feature matrix', icon: Table2 },
    { key: 'gifts', label: 'Gifts', icon: Gift },
    { key: 'coupons', label: 'Coupons', icon: Ticket },
    // Beside the prices on purpose: a price is a decision about a margin, and
    // the margin was the half nobody could see.
    { key: 'economics', label: 'Economics', icon: LineChart },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div
        style={{
          display: 'flex',
          gap: 2,
          borderBottom: '1px solid var(--neutral-300)',
        }}
      >
        {tabs.map((t) => {
          const isActive = tab === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 16px',
                fontSize: 14,
                fontWeight: isActive ? 600 : 500,
                fontFamily: 'var(--font-family)',
                color: isActive ? 'var(--blue-600)' : 'var(--neutral-700)',
                background: 'transparent',
                border: 'none',
                borderBottom: `2px solid ${isActive ? 'var(--blue-600)' : 'transparent'}`,
                cursor: 'pointer',
              }}
            >
              <Icon size={16} />
              {t.label}
            </button>
          );
        })}
      </div>

      {loading && !catalogue && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner />
        </div>
      )}

      {error && (
        <div style={{ color: 'var(--danger-900)', fontSize: 13 }}>{error}</div>
      )}

      {catalogue && tab === 'plans' && (
        <PlanList plans={catalogue.plans} canManage={canManage} onChanged={() => void load()} />
      )}

      {tab === 'economics' && <Economics />}

      {catalogue && tab === 'gifts' && <Gifts plans={catalogue.plans} canManage={canManage} />}

      {catalogue && tab === 'coupons' && <Coupons plans={catalogue.plans} canManage={canManage} />}

      {catalogue && tab === 'matrix' && (
        <FeatureMatrix
          plans={catalogue.plans}
          features={catalogue.features}
          sections={catalogue.sections}
          matrix={catalogue.matrix}
          canManage={canManage}
          onChanged={() => void load()}
        />
      )}
    </div>
  );
};
